import { Location } from 'history';
import { NavLinkProps } from 'react-router-dom';
import * as R from 'remeda';
import { pick } from 'remeda';
import { getDashboardPath } from '../../containers/AnnotationList/helpers';
import {
  automationEnginesPath,
  automationPath,
} from '../../containers/Automation/helpers';
import { datasetsPath } from '../../features/datasets/routes';
import { parse, stringify } from '../../lib/url';
import { STATISTICS_QUERY } from '../../redux/modules/localStorage/actions';
import { GroupRole } from '../../types/group';

type VisibleMenuItemsForTabContext = {
  features: {
    isLabelsEnabled?: boolean;
    isFieldManagerEnabled?: boolean;
    isDatasetsEnabled?: boolean;
    isRulesEnabled: boolean;
    isExternalStoresEnabled: boolean;
  };
};

export type MenuItemDefinition<
  Name extends string,
  ParentName extends string,
> = {
  name: Name;
  link: string;
  isVisible?: (context: VisibleMenuItemsForTabContext) => boolean;
  dataCy: string;
  translationKey: `${ParentName}.${Name}`;
  isActive?: NavLinkProps['isActive'];
  beta?: boolean;
};

export type LinkContext = { shouldDisplayNewDashboard?: boolean };

type TabDefinition<
  MName extends string,
  Name extends string,
  MenuItems extends Array<MenuItemDefinition<MName, Name>>,
> = {
  name: Name;
  roles?: GroupRole[];
  menu?: MenuItems;
  link: string | ((context?: LinkContext) => string);
  isSelected?: (location: Location<unknown>) => boolean;
};

// Helper functions that are used only to infer correct types
const tabItem = <
  MN extends string,
  N extends string,
  // WORKAROUND: [] is used instead of undefined until we have strict mode turned on
  M extends Array<MenuItemDefinition<MN, N>> = [],
>(
  t: TabDefinition<MN, N, M>
) => t;
const menuItem = <N extends string, P extends string>(
  m: MenuItemDefinition<N, P>
) => m;

export const tabs = [
  tabItem({
    name: 'documents',
    link: () => getDashboardPath(),
    isSelected: location =>
      Boolean(location.pathname.match(/^\/annotations|^\/emails|^\/documents/)),
  }),
  tabItem({
    name: 'requests',
    link: '/requests',
    isSelected: location => Boolean(location.pathname.match(/^\/requests/)),
  }),
  tabItem({
    name: 'automation',
    link: automationPath(),
    roles: ['admin', 'organization_group_admin'],
    menu: [
      menuItem({
        name: 'automationSettings',
        link: automationPath(),
        dataCy: 'settings-sidebar-automation-link',
        translationKey: 'automation.automationSettings',
      }),
      menuItem({
        name: 'aiEngines',
        link: automationEnginesPath(),
        dataCy: 'settings-sidebar-engines-link',
        translationKey: 'automation.aiEngines',
      }),
    ],
  }),
  tabItem({
    name: 'extensions',
    roles: ['admin', 'organization_group_admin'],
    link: '/extensions/my-extensions',
    menu: [
      menuItem({
        name: 'myExtensions',
        link: '/extensions/my-extensions',
        dataCy: 'settings-sidebar-extensions-link',
        translationKey: 'extensions.myExtensions',
      }),
      menuItem({
        name: 'rossumStore',
        link: '/extensions/store',
        dataCy: 'settings-sidebar-store-link',
        translationKey: 'extensions.rossumStore',
      }),
      menuItem({
        name: 'externalStore',
        link: '/extensions/external-store',
        dataCy: 'settings-sidebar-custom-store',
        translationKey: 'extensions.externalStore',
        isVisible: ({ features }) => !!features.isExternalStoresEnabled,
      }),
      menuItem({
        name: 'logs',
        link: '/extensions/logs',
        dataCy: 'settings-sidebar-extensions-logs',
        translationKey: 'extensions.logs',
      }),
    ],
  }),
  tabItem({
    name: 'statistics',
    roles: ['admin', 'organization_group_admin', 'manager'],
    link: () => {
      const items = parse(localStorage.getItem(STATISTICS_QUERY) || '');

      const validItems = pick(items, [
        'beginDate',
        'endDate',
        'groupBy',
        'queues',
        'users',
      ]);

      return !R.isEmpty(validItems)
        ? `/statistics?${stringify(validItems)}`
        : '/statistics';
    },
  }),
  tabItem({
    name: 'settings',
    roles: ['admin', 'organization_group_admin'],
    link: '/settings',
    menu: [
      menuItem({
        name: 'users',
        link: '/settings/users',
        dataCy: 'settings-users-link',
        translationKey: 'settings.users',
      }),
      menuItem({
        name: 'fieldManager',
        link: '/settings/field-manager',
        isVisible: ({ features }) => !!features.isFieldManagerEnabled,
        dataCy: 'settings-field-manager-link',
        translationKey: 'settings.fieldManager',
      }),
      menuItem({
        name: 'labels',
        link: '/settings/labels',
        isVisible: ({ features }) => !!features.isLabelsEnabled,
        dataCy: 'settings-labels-link',
        translationKey: 'settings.labels',
      }),
      menuItem({
        name: 'datasets',
        link: datasetsPath(),
        dataCy: 'settings-datasets-link',
        translationKey: 'settings.datasets',
        isVisible: ({ features }) => !!features.isDatasetsEnabled,
      }),
      menuItem({
        name: 'ruleTemplates',
        link: '/settings/rule-templates',
        isVisible: ({ features }) => features.isRulesEnabled,
        dataCy: 'settings-rule-templates-link',
        translationKey: 'settings.ruleTemplates',
      }),
    ],
  }),
];

export type Tab = (typeof tabs)[number];
type TabName = (typeof tabs)[number]['name'];
export type MenuItem = Extract<
  (typeof tabs)[number]['menu'],
  Array<unknown>
>[number];

const tabByName = <N extends TabName>(tabName: N): Extract<Tab, { name: N }> =>
  tabs.find(t => t.name === tabName) as Extract<Tab, { name: N }>;

export const tabToLink = (tab: Tab, linkContext?: LinkContext) =>
  typeof tab.link === 'string' ? tab.link : tab.link(linkContext);

export const isTabVisible = (tab: Tab, role: GroupRole) =>
  tab.roles === undefined || tab.roles.includes(role);

export const visibleMenuItemsForTab = (
  menuItems: MenuItem[],
  context: VisibleMenuItemsForTabContext
) => menuItems.filter(m => m.isVisible === undefined || m.isVisible(context));

export const stripQuery = (url: string) => {
  const startOfQuery = url.indexOf('?');

  return startOfQuery === -1 ? url : url.slice(0, startOfQuery);
};

export const isTabSelected = (tab: Tab, location: Location<unknown>) => {
  if (tab.isSelected) {
    // Custom selection logic has priority
    return tab.isSelected(location);
  }

  // Check if the tab or any of the menu items referes to the current path.
  // We ignore query part of the URL, since it usually refers to last parameters used on the target page,
  // and it shouldn't be part of the decision.
  if (location.pathname.startsWith(stripQuery(tabToLink(tab)))) {
    return true;
  }

  if (tab.menu) {
    return tab.menu.some(m => location.pathname.startsWith(stripQuery(m.link)));
  }

  return false;
};

export const defaultTab = tabByName('documents');
